import { useState } from 'react'

/**
 * Simple toggle hook - tracks the state of a boolean value.
 * Useful for actions that toggle between two states (e.g. a Collapsible component).
 *
 * The function it returns also accepts an optional argument if you need to
 * set the value to a specific state (true or false).
 *
 * Example (taken from https://github.com/streamich/react-use/blob/master/docs/useToggle.md,
 * which works the same way):
 *
 * const [on, toggle] = useToggle(false)
 * <div>
 *   <div>{on ? 'ON' : 'OFF'}</div>
 *   <button onClick={toggle}>Toggle</button>
 *   <button onClick={() => toggle(true)}>set ON</button>
 *   <button onClick={() => toggle(false)}>set OFF</button>
 * </div>
 */
export function useToggle(
    initialState: boolean
): [boolean, (newVal?: boolean) => void] {
    const [on, setOn] = useState(initialState)
    const toggle = (newVal?: boolean) =>
        setOn(typeof newVal === 'boolean' ? newVal : !on)
    return [on, toggle]
}
